/**
 * Gameless mode module.
 *
 * Manages the room-wide "gameless" flag: when set, the app UI hides the game
 * board and shows only video, without touching the active game's state. Any
 * mate can toggle this and it applies to every connected client.
 */

import type { RoomStorage, BroadcastChannel } from '../../types/infrastructure.types'
import type { SetGamelessModeArgs } from '../../schemas/action.schemas'

export interface GamelessModeContainer {
  storage: RoomStorage
  broadcastChannel: BroadcastChannel
}

/**
 * Creates gameless mode management functions for a room.
 */
export function makeGamelessMode(container: GamelessModeContainer) {
  const { storage, broadcastChannel } = container

  /**
   * Set the room's gameless flag. Broadcasts 'gamelessModeChanged' so every
   * connected client's layout stays in sync, independent of the active game.
   */
  const setGamelessMode = async (args: SetGamelessModeArgs): Promise<{ success: boolean }> => {
    const { gameless } = args
    const state = storage.getState()

    storage.setState({ ...state, gameless })

    broadcastChannel.broadcast({
      type: 'gamelessModeChanged',
      data: { gameless },
    })

    return { success: true }
  }

  return {
    setGamelessMode,
  }
}

export type GamelessModeModule = ReturnType<typeof makeGamelessMode>
